// Save payloads. Kept pure so the v1 create body stays provable by test.

import { format } from 'date-fns'
import dayjs from 'dayjs'

import { TIME_FORMAT } from 'common/constants/timeFormat'
import type { IPatientProcedure } from 'features/Home/interfaces/IInfoSchedule'
import type { EHRAppointmentFormType } from 'features/Home/schemas/infoNewPatientSchema'
import type { ScheduleAppointmentDto } from 'features/Schedule/api/schedule.types'

const toClock = (value: Date): string => format(value, TIME_FORMAT.TIME_PICKER_HH_SS)

const endOf = (start: Date, duration: number): Date =>
  dayjs(start).add(duration, 'minutes').toDate()

// A custom procedure has no billing code, as in v1.
const procedureFields = (procedure?: IPatientProcedure) =>
  procedure
    ? {
        procedure_code: procedure.isCustom ? null : procedure.code,
        procedure_description: procedure.description,
      }
    : {}

// The form holds a status code, or '' when none was picked.
type FormStatus = number | '' | null | undefined

// No status lets the backend pick Confirmed or Unconfirmed from its own site-zone today.
const statusFields = (status: FormStatus): { status_type_code?: number } =>
  status === '' || status == null ? {} : { status_type_code: status }

export type UpdateOptions = {
  procedure?: IPatientProcedure
  // Leaves the stored status alone, so a kiosk or SMS change survives the save.
  omitStatus?: boolean
}

export type CreateExtras = {
  issueIds: readonly number[]
  complaints: readonly string[]
  procedure?: IPatientProcedure
}

export const buildCreateDto = (
  values: EHRAppointmentFormType,
  { issueIds, complaints, procedure }: CreateExtras,
): ScheduleAppointmentDto => ({
  room: values.room,
  patient: {
    id: values.id,
    first_name: values.first_name,
    last_name: values.last_name,
    phone: values.phone,
    language_type_code: values.language || values.language_type_code,
  },
  consultation_staff: values.consultation_staff,
  temperature_bypass: values.temperature_bypass ? 1 : 0,
  start_time: toClock(values.time),
  end_time: toClock(endOf(values.time, values.duration)),
  category_type_code: values.category_type_code,
  title: values.title,
  comments: values.comments,
  ...statusFields(values.patient_status),
  punctuality_type_code: Number(values.punctuality_type_code),
  punctuality_minutes: values.punctuality_minutes,
  ...(issueIds.length ? { issue_ids: [...issueIds] } : {}),
  ...(complaints.length
    ? { chief_complaints: complaints.map((complaint) => ({ complaint })) }
    : {}),
  ...procedureFields(procedure),
})

export const buildUpdateDto = (
  values: EHRAppointmentFormType,
  patientId: number | undefined,
  { procedure, omitStatus = false }: UpdateOptions = {},
): ScheduleAppointmentDto & { date: string } => ({
  date: dayjs(values.date).format('YYYY-MM-DD'),
  room: values.room,
  patient: {
    id: patientId,
    first_name: values.first_name,
    last_name: values.last_name,
    phone: values.phone,
    language_type_code: values.language_type_code,
  },
  consultation_staff: values.consultation_staff,
  temperature_bypass: values.temperature_bypass ? 1 : 0,
  start_time: toClock(values.time),
  end_time: toClock(endOf(values.time, values.duration)),
  category_type_code: values.category_type_code,
  title: values.title,
  comments: values.comments,
  ...(omitStatus ? {} : statusFields(values.patient_status)),
  punctuality_type_code: Number(values.punctuality_type_code),
  punctuality_minutes: values.punctuality_minutes,
  ...procedureFields(procedure),
})
